/*
 * Copyright (c) 2022 automata-network/tgb-donation authors and contributors.
 * This software may be modified and distributed under the terms of MIT license.
 * Refer to the LICENSE file for details.
 */
import { useCallback } from 'react'

import styled, { css } from 'styled-components'

import { PrimaryButton } from 'src/components/Button'
import FlexBox from 'src/components/Common/FlexBox'
import { DonateMethodDialog } from 'src/components/Modal'
import { useModalContext } from 'src/contexts/ModalContext'
import { pxToVw } from 'src/functions/units'

import { useDonationIdentityCollectorContext, useDonorIdentityCollectorModal } from '../donation-identity-collector.hooks'

export const IdentityConfirmForm = () => {
  const { openModal } = useModalContext()
  const { closeIdentityConfirmModal, openCollectorModal } = useDonorIdentityCollectorModal()

  const { donorIdentity } = useDonationIdentityCollectorContext()

  const handleUpdateButtonClick = useCallback(() => {
    closeIdentityConfirmModal()
    openCollectorModal()
  }, [closeIdentityConfirmModal, openCollectorModal])

  const handleProceedButtonClick = useCallback(() => {
    closeIdentityConfirmModal()
    openModal(<DonateMethodDialog />, 'donate-method', {
      dialogTitle: 'Choose donation method'
    })
  }, [closeIdentityConfirmModal, openModal])

  return (
    <>
      <StyledWireDetailRow justifyContent="space-between" alignItems="center">
        <span>Donor Type:</span>
        <FlexBox alignItems="center" columnGap={9}>
          <em>{donorIdentity?.type === 'national_id' ? 'Individual' : donorIdentity?.type === 'uen' ? 'Company' : 'Loading...'}</em>
        </FlexBox>
      </StyledWireDetailRow>

      <StyledHorizontalLine />

      <StyledWireDetailRow justifyContent="space-between" alignItems="center">
        <span>{!donorIdentity || !donorIdentity.type || donorIdentity.type === 'national_id' ? 'Name' : 'Company Name'}:</span>
        <FlexBox alignItems="center" columnGap={9}>
          <em>{donorIdentity?.name || 'Loading...'}</em>
        </FlexBox>
      </StyledWireDetailRow>

      <StyledHorizontalLine />

      <StyledWireDetailRow justifyContent="space-between" alignItems="center">
        <span>{!donorIdentity || !donorIdentity.type || donorIdentity.type === 'national_id' ? 'Identity Card Number' : 'Unique Entity Number (UEN)'}:</span>
        <FlexBox alignItems="center" columnGap={9}>
          <em>{donorIdentity?.ic_number || 'Loading...'}</em>
        </FlexBox>
      </StyledWireDetailRow>

      <StyledHorizontalLine />

      <StyledWireDetailRow justifyContent="space-between" alignItems="center">
        <span>Email Address:</span>
        <FlexBox alignItems="center" columnGap={9}>
          <em>{donorIdentity?.email || 'Loading...'}</em>
        </FlexBox>
      </StyledWireDetailRow>

      <StyledHorizontalLine />

      <StyledWireDetailRow justifyContent="space-between" alignItems="center">
        <span>Phone Number:</span>
        <FlexBox alignItems="center" columnGap={9}>
          <em>{donorIdentity?.phone_number || 'Loading...'}</em>
        </FlexBox>
      </StyledWireDetailRow>

      <StyledHorizontalLine />

      <FlexBox columnGap={24}>
        <StyledUpdateButton outlined size="large" onClick={handleUpdateButtonClick}>
          Update My Identity
        </StyledUpdateButton>
        <StyledProceedButton size="large" onClick={handleProceedButtonClick}>
          Proceed to Donation Method Selection
        </StyledProceedButton>
      </FlexBox>
    </>
  )
}

const StyledWireDetailRow = styled(FlexBox)`
  margin-block: ${pxToVw(-16)};

  > span {
    font-weight: 500;
    font-size: ${pxToVw(20)};
    color: ${({ theme }) => theme.colors.gray500};
  }

  em {
    font-size: ${pxToVw(20)};
    color: ${({ theme }) => theme.colors.gray};
  }

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      margin-block: ${pxToVw(-16, 720)};

      > span {
        font-size: ${pxToVw(20, 720)};
      }

      em {
        font-size: ${pxToVw(20, 720)};
      }
    `}
  `}
`

const StyledHorizontalLine = styled.hr`
  margin-block: 1.5625vw;
  border: none;
  border-top: solid 1px ${({ theme }) => theme.colors.gray200};

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      margin-block: ${pxToVw(30, 720)};
    `}
  `}
`

const StyledUpdateButton = styled(PrimaryButton)`
  flex-shrink: 0;
`

const StyledProceedButton = styled(PrimaryButton)`
  flex: 1;
`
